<template>
  <div id="domains_section" class="flex flex-col gap-6">
    <div class="flex justify-between items-center">
      <h1 class="text-xl font-semibold">Domains</h1>
      <div class="flex gap-2">
        <a-button class="primary_medium" @click="navigateToGenerator">
          Generate Domains
        </a-button>
      </div>
    </div>

    <div
      v-if="initialLoading"
      class="flex flex-col items-center justify-center gap-2 mt-20"
    >
      <CustomLoader :size="28" />
    </div>

    <EmptyScreen
      source="domains"
      text="No domains found!"
      v-if="domains?.length === 0 && !initialLoading"
    />

    <div v-if="domains?.length > 0 && !initialLoading">
      <CustomTable
        :headers="DOMAINS_TABLE_HEADERS"
        :data="domains"
        prefixImageBorderRadius="0%"
        :custom-column-render="customColumnRender"
        :custom-row="customRow"
        @refresh="fetchDomains"
      />
    </div>
  </div>
</template>

<script setup>
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import { onMounted, ref, defineComponent, h } from "vue";
import DashboardService from "../../api/DashboardService.js";
import { message } from "ant-design-vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { useRouter } from "vue-router";
import imagePath from "@/src/paths/imagePath.js";
import TagBubble from "@/src/components/TagBubble.vue";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";
import {
  DOMAINS_TABLE_HEADERS,
  LOADING_TIMEOUT,
  DOMAIN_STATUS,
  DEFAULT_VALUES,
} from "@/src/constants/domainsConstants.js";

const router = useRouter();
const domains = ref([]);
const initialLoading = ref(true);

const MailboxBubbles = defineComponent({
  name: "MailboxBubbles",
  components: { TagBubble },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const handleMailboxClick = () => {
      router.push("/settings#mailboxes");
    };

    const emptySlot = () => {
      const handleClick = () => {
        window.open("/settings/mailboxes/generate", "_blank");
      };

      return h(
        "div",
        {
          class: "text-sm secondary_color cursor-pointer hover:opacity-80",
          style: {
            display: "flex",
            alignItems: "center",
            gap: "2px",
          },
          onClick: (event) => {
            event.stopPropagation();
            handleClick();
          },
        },
        [
          "No Mailbox Generated",
          h("img", {
            src: imagePath.neutral.grey.linkGreyIcon,
            alt: "link icon",
          }),
        ]
      );
    };

    return () => {
      return h(
        "div",
        {
          style: {
            padding: "16px 12px",
          },
          onClick: (event) => {
            event.stopPropagation();
            handleMailboxClick();
          },
        },
        [
          h(
            TagBubble,
            {
              items: props.value || [],
              maxVisible: 3,
            },
            {
              empty: emptySlot,
            }
          ),
        ]
      );
    };
  },
});

const customColumnRender = {
  mailboxes: MailboxBubbles,
};

const navigateToGenerator = () => {
  router.push("/generate-domains");
};

const showDomainDNS = async (domainId) => {
  try {
    const domain = domains.value.find((d) => d.id === domainId);
    const domainName = domain ? domain.domain : "Domain";

    router.push({
      name: "domain-dns",
      params: { id: domainId },
      query: { domain: domainName },
    });
  } catch (error) {
    message.error("Failed to navigate to DNS records page");
  }
};

const customRow = (record) => {
  if (!record || !record.id) {
    return {};
  }

  return {
    onClick: () => {
      showDomainDNS(record.id);
    },
    class: "cursor-pointer",
  };
};

const fetchDomains = async () => {
  initialLoading.value = true;

  try {
    const response = await DashboardService.getAllDomains();
    let domainsData = [];

    if (Array.isArray(response)) {
      domainsData = response;
    } else if (response?.success && Array.isArray(response.data)) {
      domainsData = response.data;
    } else if (response && typeof response === "object") {
      if (response.id && response.sld && response.tld) {
        domainsData = [response];
      } else if (response.data && Array.isArray(response.data)) {
        domainsData = response.data;
      }
    }

    if (domainsData && domainsData.length > 0) {
      domains.value = domainsData
        .map((item) => {
          if (!item) return null;

          try {
            let statusStr;

            if (item?.status === undefined || item?.status === null) {
              statusStr = DOMAIN_STATUS.UNKNOWN;
            } else if (typeof item?.status === "object") {
              if (item?.status?.value) {
                statusStr = String(item?.status?.value);
              } else if (item?.status?.status) {
                statusStr = String(item?.status?.status);
              } else {
                statusStr = DOMAIN_STATUS.UNKNOWN;
              }
            } else {
              statusStr = String(item?.status);
            }

            statusStr =
              statusStr.charAt(0).toUpperCase() +
              statusStr.slice(1).toLowerCase();

            return {
              id: String(item?.id || ""),
              domain:
                item?.sld && item?.tld
                  ? `${item?.sld}.${item?.tld}`
                  : DEFAULT_VALUES.UNKNOWN_DOMAIN,
              forwardToURL: String(
                item?.forwardToDomain || DEFAULT_VALUES.EMPTY_FORWARD
              ),
              mailboxes: item?.emails || [],
              status: statusStr,
              createdAt: item?.createdAt
                ? formatTimestamp(item?.createdAt, "timestamp")
                : DEFAULT_VALUES.UNKNOWN_DATE,
            };
          } catch (err) {
            return {
              id: String(item?.id || "unknown"),
              domain: DEFAULT_VALUES.ERROR_DOMAIN,
              forwardToURL: DEFAULT_VALUES.EMPTY_FORWARD,
              mailboxes: [],
              status: DOMAIN_STATUS.ERROR,
              createdAt: DEFAULT_VALUES.UNKNOWN_DATE,
            };
          }
        })
        .filter(Boolean);
    } else {
      domains.value = [];
    }
  } catch (error) {
    message.error("Failed to fetch domains");
    domains.value = [];
  } finally {
    initialLoading.value = false;
  }
};

onMounted(() => {
  fetchDomains().catch(() => {
    initialLoading.value = false;
  });

  setTimeout(() => {
    if (initialLoading.value) {
      initialLoading.value = false;
    }
  }, LOADING_TIMEOUT);
});
</script>
